import { RefObject, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatMoney } from '@belong/common';
import { IconAlert } from '@belong/icons';
import { ButtonSelector, Divider, Text } from '@belong/ui';
import { MaintenanceGroupBundleModel, MaintenanceModel } from 'api/models';
import clsx from 'clsx';
import Collapsible from 'components/Collapsible/Collapsible';
import NameValueTable from 'components/NameValueTable/NameValueTable';
import { useMaintenanceHasPendingQuoteApproval } from 'maintenance/hooks/useMaintenanceHasPendingQuoteApproval';
import { useMaintenanceProposalOptions } from 'maintenance/hooks/useMaintenanceProposalOptions';
import { usePreloadGroupMaintenanceTasks } from 'maintenance/hooks/usePreloadGroupMaintenanceTasks';
import { workOrderStatus } from 'maintenance/status';
import { fetchTaskByTaskId } from 'store/redux/maintenance/actions';
import { selectMaintenanceById } from 'store/redux/maintenance/selectors';
import { MaintenanceApprovalAndResponsibility } from '../MaintenanceApprovalAndResponsibility/MaintenanceApprovalAndResponsibility';
import { MaintenanceCost } from '../MaintenanceCost/MaintenanceCost';
import { useMaintenanceCostDisplayData } from '../MaintenanceCost/use-maintenance-cost-display-data';
import { SplitInfoBox } from '../split-infobox/split-infobox';
import styles from './Proposals.module.css';

type ProposalsProps = {
  maintenance: MaintenanceModel;
  userRole: 'Resident' | 'Homeowner';
  fetchTask: () => Promise<void>;
  showScheduleCall?: boolean;
  onScheduleHomecareSpecialistCall: () => void;
  accountResponsibilityRef: RefObject<HTMLDivElement>;
};

type ProposalContentProps = {
  bundle: MaintenanceGroupBundleModel;
  userRole: 'Resident' | 'Homeowner';
  fetchTask: () => Promise<void>;
  showScheduleCall?: boolean;
  paddingHorizontal?: boolean;
  onScheduleHomecareSpecialistCall?: () => void;
  accountResponsibilityRef: RefObject<HTMLDivElement>;
};

type ProposalsContentProps = {
  maintenance: MaintenanceModel;
  userRole: 'Resident' | 'Homeowner';
  fetchTask: () => Promise<void>;
  showScheduleCall?: boolean;
  onScheduleHomecareSpecialistCall?: () => void;
  accountResponsibilityRef: RefObject<HTMLDivElement>;
};

const ProposalContent = ({
  bundle,
  userRole,
  fetchTask,
  showScheduleCall,
  onScheduleHomecareSpecialistCall,
  accountResponsibilityRef,
  paddingHorizontal,
}: ProposalContentProps) => {
  // `items`, and each item's `maintenance`/`maintenancePayments`, are optional in
  // the generated model, but a proposal bundle always carries them: every read
  // below except the first dereferences them directly, so an absent `items` was
  // already a crash here rather than an empty render.
  const items = bundle.items!;
  const optionItems = items.filter((item) => !item.isTBD);
  const approvedItem = items.find((item) =>
    item.maintenancePayments!.some(
      (payment) => payment.consentStatus === 'Approved',
    ),
  );
  const [selectedProposalId, setSelectedProposalId] = useState(
    approvedItem
      ? approvedItem.maintenance!.uniqueId
      : optionItems.length > 0
        ? optionItems[0]!.maintenance!.uniqueId
        : undefined,
  );
  const options = useMaintenanceProposalOptions(
    optionItems,
    selectedProposalId,
  );
  const dispatch = useDispatch();

  const proposal = items.find(
    (item) => item.maintenance!.uniqueId === selectedProposalId,
  );
  const taskOptionIndex = items.findIndex(
    (item) => item.maintenance!.uniqueId === selectedProposalId,
  );
  const taskOptionNumber =
    items.length > 1 && taskOptionIndex !== -1
      ? taskOptionIndex + 1
      : undefined;
  const proposalMaintenanceUniqueId = proposal?.maintenance?.uniqueId;
  const proposalMaintenance = useSelector(
    selectMaintenanceById(proposalMaintenanceUniqueId),
  );
  const hasMultipleOptions = optionItems.length > 1;
  const aProposalHasBeenApproved = items.some((item) =>
    item.maintenancePayments!.some(
      (payment) => payment.consentStatus === 'Approved',
    ),
  );

  const onChangeSelectedProposal = (value: string) => {
    setSelectedProposalId(value);
  };

  const onConsentChange = async () => {
    setTimeout(async () => {
      for (const item of items) {
        const maintenanceUniqueId = item.maintenance?.uniqueId;
        try {
          await dispatch(fetchTaskByTaskId(maintenanceUniqueId));
        } catch (error) {
          console.log(error);
        }
      }
    }, 3000);

    fetchTask();
  };

  useEffect(() => {
    if (proposalMaintenanceUniqueId && !proposalMaintenance) {
      try {
        dispatch(fetchTaskByTaskId(proposalMaintenanceUniqueId));
      } catch (error) {
        console.log(error);
      }
    }
  }, [dispatch, proposalMaintenanceUniqueId, proposalMaintenance]);

  return (
    <div>
      {/*
       * `options` is a 1:1 map of `optionItems`, so reaching this branch means
       * `optionItems.length > 1` — which is exactly the case where the state above
       * was initialised to an id rather than left undefined.
       */}
      {options.length > 1 && (
        <div className={clsx('my-xl', paddingHorizontal && 'mx-sm')}>
          <div className="hidden md:contents">
            <ButtonSelector
              options={options}
              onChange={onChangeSelectedProposal}
              itemWidth="small"
              itemHeight="extra-small"
              value={selectedProposalId!}
            />
          </div>
          <div className="contents md:hidden">
            <ButtonSelector
              options={options}
              onChange={onChangeSelectedProposal}
              itemWidth="fluid"
              itemHeight="extra-small"
              value={selectedProposalId!}
            />
          </div>
        </div>
      )}

      {proposalMaintenance && (
        <MaintenanceCost
          task={proposalMaintenance}
          taskOptionNumber={hasMultipleOptions ? taskOptionNumber : undefined}
          userRole={userRole}
          onConsentChange={onConsentChange}
          accountResponsibilityRef={accountResponsibilityRef}
          showScheduleCall={showScheduleCall}
          onScheduleHomecareSpecialistCall={onScheduleHomecareSpecialistCall}
          showItemPrices={false}
          showQuoteApprovalSection={!aProposalHasBeenApproved}
          paddingHorizontal={paddingHorizontal}
        />
      )}

      {proposalMaintenance &&
        proposalMaintenance?.payments?.some(
          (payment) =>
            payment.consentStatus === 'Approved' ||
            payment.consentStatus === 'Rejected',
        ) && (
          <div className={paddingHorizontal ? 'mx-sm' : ''}>
            <Divider borderStyle="dashed" />
            <div className="mt-sm">
              <MaintenanceApprovalAndResponsibility
                collapsable={false}
                task={proposalMaintenance}
                title=""
                payments={proposalMaintenance.payments.filter(
                  (payment) =>
                    payment.consentStatus === 'Approved' ||
                    payment.consentStatus === 'Rejected',
                )}
              />
            </div>
          </div>
        )}
    </div>
  );
};

const ProposalsContent = ({
  maintenance,
  userRole,
  fetchTask,
  showScheduleCall,
  onScheduleHomecareSpecialistCall,
  accountResponsibilityRef,
}: ProposalsContentProps) => {
  // Both optional in the generated model; this component is only rendered for a
  // grouped maintenance, which always has them, and every read here already
  // dereferenced them directly.
  const subtasks = maintenance.subtasks!;
  const priceRange = maintenance.priceRange!;
  const totalPriceRange =
    priceRange.lowerBoundTotal === priceRange.upperBoundTotal
      ? formatMoney(priceRange.lowerBoundTotal)
      : `${formatMoney(priceRange.lowerBoundTotal)} - ${formatMoney(
          priceRange.upperBoundTotal,
        )}`;
  const { financePlanId, hasEligibleOpenBalance } =
    useMaintenanceCostDisplayData({
      task: maintenance,
      userRole,
    });

  if (subtasks.length > 1) {
    return (
      <>
        {subtasks.map((subtask, index) => {
          const subtaskItems = subtask.items!;
          const subtasksHasBeenApproved = subtaskItems.some((item) =>
            item.maintenancePayments!.some(
              (payment) => payment.consentStatus === 'Approved',
            ),
          );
          let primaryTask = subtaskItems.find(
            (item) =>
              item.maintenance!.workOrder?.createdReason === 'Primary' ||
              item.maintenance!.workOrder?.createdReason === 'AdditionalWork',
          );
          primaryTask = primaryTask || subtaskItems[0];
          const approvedItem = subtaskItems.find((item) =>
            item.maintenancePayments?.some(
              (payment) => payment.consentStatus === 'Approved',
            ),
          );
          const bundleOptions =
            subtaskItems.length > 1
              ? subtaskItems.filter((item) => !item.isTBD)
              : [];
          const bundleOptionsLabel =
            bundleOptions.length > 1 ? `${bundleOptions.length} Options` : '';
          const optionsAvailableLabel = !subtasksHasBeenApproved
            ? ' Available'
            : '';
          const workStatus = subtaskItems.some(
            (item) =>
              item.maintenance!.workOrder?.status === 'WaitingForQuoteApproval',
          )
            ? 'WaitingForQuoteApproval'
            : primaryTask?.maintenance?.workOrder?.status;
          const workOrderStatusValue = approvedItem
            ? approvedItem.maintenance!.workOrder?.status ===
              'WaitingForQuoteApproval'
              ? 'WorkInProgress'
              : approvedItem.maintenance!.workOrder?.status
            : workStatus;

          const subtaskPriceRange = subtask.priceRange!;
          let priceRange;
          if (subtaskPriceRange.lowerBoundTotal > 0) {
            priceRange =
              subtaskPriceRange.lowerBoundTotal ===
              subtaskPriceRange.upperBoundTotal
                ? formatMoney(subtaskPriceRange.lowerBoundTotal)
                : `${formatMoney(subtaskPriceRange.lowerBoundTotal)} - ${formatMoney(
                    subtaskPriceRange.upperBoundTotal,
                  )}`;
          }

          return (
            <div className="mb-sm" key={index}>
              <Collapsible
                collapsed={false}
                title={primaryTask?.maintenance?.summary}
                defaultClassName={clsx(
                  'mt-md rounded border-solid border-gray bg-light-gray',
                  styles.proposalCollapsible,
                )}
                className="px-sm pt-sm"
                openMarginBottom="mb-xs"
                titleComponent={
                  <div className="flex w-full flex-col pb-sm">
                    <div className="flex w-full flex-row justify-between">
                      <Text
                        className="capitalize"
                        fontWeight="semibold"
                        variant="body"
                      >
                        {primaryTask?.maintenance?.summary}
                      </Text>
                      {priceRange && (
                        <div className="pr-sm">
                          <span className="text-body font-semibold">
                            {priceRange}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="text-p1 font-semibold">
                      {workOrderStatusValue && (
                        <span
                          className={
                            workOrderStatus[workOrderStatusValue].color
                          }
                        >
                          {workOrderStatus[workOrderStatusValue].label}
                        </span>
                      )}
                      <span className="font-regular">
                        {workOrderStatusValue && bundleOptionsLabel
                          ? ` | ${bundleOptionsLabel}${optionsAvailableLabel}`
                          : bundleOptionsLabel}
                      </span>
                    </span>
                  </div>
                }
                inner={
                  <ProposalContent
                    key={index}
                    bundle={subtask}
                    userRole={userRole}
                    fetchTask={fetchTask}
                    showScheduleCall={showScheduleCall}
                    onScheduleHomecareSpecialistCall={
                      onScheduleHomecareSpecialistCall
                    }
                    accountResponsibilityRef={accountResponsibilityRef}
                    paddingHorizontal
                  />
                }
              />
            </div>
          );
        })}
        <NameValueTable
          boldValue
          items={[
            {
              name: 'Total',
              value: totalPriceRange,
            },
          ]}
        />
        {!maintenance.isLegacyInvoicing && userRole === 'Homeowner' && (
          <div className="mt-md">
            <SplitInfoBox
              financePlanId={financePlanId}
              hasOpenBalance={hasEligibleOpenBalance}
              isMaintenanceCompleted={maintenance.result === 'Completed'}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {subtasks.map((subtask, index) => (
        <div className="mt-md" key={index}>
          <ProposalContent
            bundle={subtask}
            userRole={userRole}
            fetchTask={fetchTask}
            showScheduleCall={showScheduleCall}
            onScheduleHomecareSpecialistCall={onScheduleHomecareSpecialistCall}
            accountResponsibilityRef={accountResponsibilityRef}
          />
        </div>
      ))}
      <NameValueTable
        boldValue
        items={[
          {
            name: 'Total',
            value: totalPriceRange,
          },
        ]}
      />
    </>
  );
};

export const Proposals = ({
  maintenance,
  userRole,
  fetchTask,
  showScheduleCall,
  onScheduleHomecareSpecialistCall,
  accountResponsibilityRef,
}: ProposalsProps) => {
  // Optional in the generated model. The single-subtask branch below indexes
  // `subtasks[0]` unguarded, so this component already required at least one.
  const subtasks = maintenance.subtasks!;
  const hasPendingQuotesToApprove = useMaintenanceHasPendingQuoteApproval(
    userRole,
    maintenance,
  );
  const { financePlanId, hasEligibleOpenBalance } =
    useMaintenanceCostDisplayData({
      task: maintenance,
      userRole,
    });

  usePreloadGroupMaintenanceTasks(maintenance);

  return (
    <>
      <Collapsible
        title="Scope Of Work"
        collapsed={false}
        titleComponent={
          hasPendingQuotesToApprove ? (
            <div className="flex flex-row">
              <Text className="capitalize" fontWeight="semibold" variant="h3">
                Scope Of Work
              </Text>
              <IconAlert width={26} className="ml-xs" />
            </div>
          ) : null
        }
        inner={
          subtasks.length > 1 ? (
            <ProposalsContent
              maintenance={maintenance}
              userRole={userRole}
              fetchTask={fetchTask}
              showScheduleCall={showScheduleCall}
              onScheduleHomecareSpecialistCall={
                onScheduleHomecareSpecialistCall
              }
              accountResponsibilityRef={accountResponsibilityRef}
            />
          ) : (
            <>
              <ProposalContent
                bundle={subtasks[0]!}
                userRole={userRole}
                fetchTask={fetchTask}
                showScheduleCall={showScheduleCall}
                onScheduleHomecareSpecialistCall={
                  onScheduleHomecareSpecialistCall
                }
                accountResponsibilityRef={accountResponsibilityRef}
                paddingHorizontal={false}
              />
              {!maintenance.isLegacyInvoicing && userRole === 'Homeowner' && (
                <div className="mt-sm">
                  <SplitInfoBox
                    financePlanId={financePlanId}
                    hasOpenBalance={hasEligibleOpenBalance}
                    isMaintenanceCompleted={maintenance.result === 'Completed'}
                  />
                </div>
              )}
            </>
          )
        }
      />
    </>
  );
};
